import React, { useState, useEffect } from 'react'
import Layout from '../../layouts/Layout'
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { getShippingZoneAPI, getRegionListAPI, updateShippingZoneAPI } from '../../services/api/ShippingAPI';
import Swal from 'sweetalert2';
import Select from "react-select";
import { FaStarOfLife } from 'react-icons/fa';
import { useSelector } from "react-redux";
import { Link, useNavigate, useParams } from 'react-router-dom';
import Loader from '../components/Loader';
import TagsInput from 'react-tagsinput';

const EditShippingZone = () => {
    const { id } = useParams();
    const [loading, setLoading] = useState(false);
    const [options, setOptions] = useState([]);
    const [selectedOptions, setSelectedOptions] = useState([]);

    const user = useSelector((state) => state.LoginReducer.authData);
    const token = user.data.token;
    const navigate = useNavigate();

    useEffect(() => {
        const fetchRegion = async () => {
            try {
                const data = await getRegionListAPI(token);
                if (data.status) {
                    const options = data.data.map(item => ({
                        value: item.id,
                        label: item.name,
                    }));
                    setOptions(options);
                }
            } catch (error) {
                console.error('Error fetching regions list:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchRegion();
    }, [token]);

    const handleTagChange = (newTags) => {
        formik.setFieldValue('pincode', newTags);
    };

    useEffect(() => {
        const fetchShippingMethod = async () => {
            try {
                const data = await getShippingZoneAPI(id, token);                
                if (data === null) {
                    navigate('/404');
                } else {
                    formik.setValues({
                        name: data.name,
                        region_ids: data.region_id,
                        pincode: data.pincode
                    });
                }                  
                
            } catch (error) {
                Swal.fire({
                    title: 'Error!',
                    text: 'Could not fetch blog data.',
                    icon: 'error',
                    confirmButtonText: 'OK',
                });
            }
        };

        fetchShippingMethod();
    }, [id]);

    const handleChange = (selected) => {
        formik.setFieldValue('region_ids', selectedOptions.map(option => option.value));
        setSelectedOptions(selected);
    };

    const validationSchema = Yup.object({
        name: Yup.string()
            .min(3, 'Name must be at least 3 characters long')
            .required('Name is required'),
        region_ids: Yup.array()
            .min(1, 'At least one region is required')
            .of(Yup.number().required('Each region must be a valid id')),
        pincode: Yup.array()
            .required('Tag cannot be empty')
            .min(1, 'At least one tag is required')
    });

    const formik = useFormik({
        initialValues: {
            name: '',
            region_ids: [],
            pincode: [],
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            setLoading(true);
            const formData = new FormData();
            formData.append('name', values.name);
            values.region_ids.forEach(item => {
                formData.append('region_ids[]', item);
            });
            values.pincode.forEach(pin => {
                formData.append('pincode[]', pin);
            });
            await updateShippingZone(formData);
        },
    });

    const updateShippingZone = async (formData) => {
        try {
            const data = await updateShippingZoneAPI(formData, id, token);
            setLoading(false);
            if (data.status) {
                Swal.fire({
                    title: 'Shipping zone updated!',
                    text: 'The shipping zone has been updated successfully.',
                    icon: 'success',
                    confirmButtonText: 'OK',
                }).then(() => {
                    navigate('/shippings');
                });
            }
        } catch (error) {
            setLoading(false);
            Swal.fire({
                title: 'Error!',
                text: 'Something went wrong please try again.',
                icon: 'error',
                confirmButtonText: 'OK',
            });
        }
    };

    const customStyles = {
        control: (provided) => ({
            ...provided,
            borderRadius: 'none',
            borderColor: 'none',
            boxShadow: 'none',
            borderStyle: 'none',
            '&:hover': {
                borderColor: '#007bff',
            },
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#007bff' : null,
            color: state.isSelected ? 'white' : 'black',
        }),
    };
    if (loading) {
        return <Loader />;
    }
    return (
        <Layout title="Edit Shipping Zone">
            <div className="container table-container">
                <form onSubmit={formik.handleSubmit}>
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12 col-12">
                                <div className="form-group py-2 required">
                                    <label htmlFor="name">Name <FaStarOfLife className='text-danger' /></label>
                                    <input type="text" className="form-control mt-1" name="name" id="name" placeholder="Enter name" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.name || ''} />
                                </div>
                                {formik.touched.name && formik.errors.name ? (<label className="text-danger">{formik.errors.name}</label>) : null}
                            </div>
                            <div className="col-md-12 col-12">
                                <div className="form-group py-3 required">
                                    <label htmlFor="region_ids">Region <FaStarOfLife className='text-danger' /></label>
                                    <Select isMulti name='region_ids' id='region_ids'
                                        value={options.filter(option => formik.values.region_ids.includes(option.value))}
                                        onChange={handleChange}
                                        options={options}
                                        className="form-control mt-1 p-0"
                                        classNamePrefix="select"
                                        styles={customStyles}
                                    />
                                </div>
                                {formik.touched.region_ids && formik.errors.region_ids ? (<label className="text-danger">{formik.errors.region_ids}</label>) : null}
                            </div>
                            <div className="col-md-12 col-12">
                                <div className="form-group py-3 required">
                                    <TagsInput value={formik.values.pincode}  name="pincode" id="pincode" onChange={handleTagChange} inputProps={{
                                        placeholder: 'Type and press Enter to add a pincode',
                                        className: 'form-control m-1',
                                    }} />
                                    {formik.touched.pincode && formik.errors.pincode ? (<label className="text-danger">{formik.errors.pincode}</label>) : null}
                                </div>
                            </div>
                            <div className="row mt-3">
                                <div className="col-md-6 col-12 d-flex gap-3">
                                    <Link to={'/shippings'} type="submit" className="btn btn-dark cancel_btn">Cancel</Link>
                                    <button type="submit" className="btn btn-primary submit_btn">Submit</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </Layout>
    )
}

export default EditShippingZone;